import _ from 'lodash';

import { HITL } from '@/api';
import { AgentStatusRequest } from './types';
import { useAgentsStore, useAgentsSetters } from './use-agents-store';
import { useAuth } from '@/composables/use-auth';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';
import {
  STATUS_CHANGE_ACTION_TYPE,
  STATUS_CHANGE_REASON,
  STATUS_CHANGE_SOURCE,
  DEFAULT_EXPIRE_IN,
} from '@/constants';

export const useAgentStatusActions = () => {
  const store = useAgentsStore();
  const setters = useAgentsSetters();
  const { userId } = useAuth();

  /**
   * Return agent's current status if it is not expired
   * or determine default status based on status settings.
   */
  const getAgentInitialStatus = () => {
    const { statusData } = store.agent!;
    const expireIn = statusData.expireIn || DEFAULT_EXPIRE_IN;
    const lastStatusUpdate = new Date(statusData.lastStatusUpdate || 0);

    /* When there is a fresh status on server - simply ping to update timestamp */
    const difference = Date.now() - lastStatusUpdate.getTime();
    if (difference < expireIn * 1000) {
      return 'ping';
    }

    /* Default status is always available after running settings migration*/
    const defaultStatusKey = store.settings.defaultStatusKey!;
    const defaultStatus = _.get(store.settings.statuses, defaultStatusKey)!;
    return { ...defaultStatus };
  };

  /**
   * We assume that "Set Initial Status" request is made after "Active Agent" request is made,
   * so we can re-use status data.
   */
  const initializeActiveAgentStatus = async () => {
    const response = getAgentInitialStatus();
    const statusChangeContext = {
      type: STATUS_CHANGE_ACTION_TYPE.AUTO,
      reason: STATUS_CHANGE_REASON.INITIAL,
    };

    store.isStatusInitializing = true;
    response === 'ping'
      ? await HITL.agents.pingAgentStatus()
      : await setActiveAgentStatus({ ...response, statusChangeContext });
    store.isStatusInitializing = false;
  };

  /** Main method for updating active agent's status */
  const setActiveAgentStatus = async (status: AgentStatusRequest) => {
    if (!store.agent) {
      return;
    }

    const agentId = store.agent.id;
    store.isStatusLoading = true;

    try {
      const statusChangeContext = {
        ...status.statusChangeContext,
        source: STATUS_CHANGE_SOURCE,
      };

      await HITL.agents.setAgentStatus({
        version: 2,
        agentId,
        statusData: _.omit(status, ['statusChangeContext']) as AgentStatusRequest,
        statusChangeContext,
        notifyStatusChange: false,
      });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    }

    setters.setAgentStatus({ status: { agentId, ...status }, isUIAction: true });
    store.statusUpdateTimestamp = Date.now();
    store.isStatusLoading = false;
  };

  /** Try to restore agent status after disconnect */
  const reInitializeActiveAgentStatus = async () => {
    try {
      store.isStatusDesync = true;
      store.isStatusInitializing = true;

      const response = await HITL.agents.getAgents({ agentIds: [userId.value] });
      const [agent] = response.data;

      const lastStatusUpdate = new Date(agent.statusData?.lastStatusUpdate || 0);
      const expireIn = agent.statusData?.expireIn || DEFAULT_EXPIRE_IN;
      const difference = Date.now() - lastStatusUpdate.getTime();

      /* If agent has a fresh status on server - confirm it by pinging */
      if (difference < expireIn * 1000) {
        await HITL.agents.pingAgentStatus();
        setters.setAgentStatus({
          status: { agentId: userId.value, ...agent.statusData as AgentStatusRequest },
          isUIAction: true,
        });

        store.isStatusDesync = false;
        store.isStatusInitializing = false;
        return;
      }

      /* Try to restore current UI status */
      if (store.activeAgentStatusOption?.canToggle.includes('off')) {
        await setActiveAgentStatus({
          ...store.activeAgentStatusOption,
          statusChangeContext: {
            type: STATUS_CHANGE_ACTION_TYPE.AUTO,
            reason: STATUS_CHANGE_REASON.RESTORE,
          },
        });
      } else {
        /* Prevent user from being locked into a "readonly" status by setting a default status instead */
        const defaultStatusKey = store.settings.defaultStatusKey!;
        const defaultStatus = _.get(store.settings.statuses, defaultStatusKey)!;

        await setActiveAgentStatus({
          ...defaultStatus,
          statusChangeContext: {
            type: STATUS_CHANGE_ACTION_TYPE.AUTO,
            reason: STATUS_CHANGE_REASON.RESTORE,
          },
        });
      }

      store.isStatusDesync = false;
      store.isStatusInitializing = false;
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    }
  };

  return {
    initializeActiveAgentStatus,
    reInitializeActiveAgentStatus,
    setActiveAgentStatus,
  };
};
